export type AgentOsStudioMode = "owner" | "builder" | "expert";

export const AGENT_OS_MODES: ReadonlyArray<{ id: AgentOsStudioMode; label: string; description: string }> = [
  { id: "owner", label: "Owner", description: "Missionen, Freigaben, Status und Ergebnisse." },
  { id: "builder", label: "Builder", description: "Versionierte Agent-OS-Entwürfe und Tests." },
  { id: "expert", label: "Expert", description: "Schemas, Hashes, Events und Attestation." },
];

/**
 * SIMO-CODEX-CLAUDE-EXECUTION-LANE-1 — S6: Rolle und Ansicht sind zwei Dinge.
 *
 * Der Studio-Mode war faktisch als Berechtigungsschalter benutzt worden
 * (`mode !== "owner"`). Das war doppelt falsch: der Owner — der die Rechte
 * tatsächlich hat — bekam die Mutations-Oberflächen gesperrt, und ein
 * Operator/Readonly bekam sie durch einen simplen Moduswechsel eingeschaltet und
 * lief dann in den serverseitigen `403 agent_os_mutation_forbidden`.
 *
 * Korrekte Trennung ab hier:
 *
 *  * Die BERECHTIGUNG kommt aus der Session-Rolle. Sie spiegelt genau das
 *    serverseitige Gate (`requireOwner` → owner/admin) und ist damit ehrlich:
 *    die UI zeigt nur an, was der Server auch zulässt.
 *  * Der MODE steuert ausschließlich die Darstellungstiefe. Er kann nie
 *    Rechte geben und nie Rechte nehmen.
 */
export type AgentOsStudioRole = "owner" | "admin" | "operator" | "readonly";

/** Spiegelt `canApproveOwnerDevice` auf dem Server: nur owner und admin. */
export const AGENT_OS_MUTATION_ROLES: ReadonlyArray<AgentOsStudioRole> = ["owner", "admin"];

/**
 * Fail-closed: eine unbekannte, fehlende oder noch nicht geladene Rolle
 * berechtigt zu nichts.
 */
export function canMutateAgentOs(role: AgentOsStudioRole | null | undefined): boolean {
  return role === "owner" || role === "admin";
}

export type AgentOsStudioDepth = "outcome" | "authoring" | "internals";

/**
 * Die Tiefe ist eine reine Darstellungsentscheidung: Ergebnisse, Autoring oder
 * Interna. Sie sagt nichts darüber aus, was der Nutzer darf.
 */
export function agentOsStudioDepth(mode: AgentOsStudioMode): AgentOsStudioDepth {
  if (mode === "owner") return "outcome";
  if (mode === "builder") return "authoring";
  return "internals";
}

export function showsAgentOsInternals(mode: AgentOsStudioMode): boolean {
  return agentOsStudioDepth(mode) === "internals";
}

export type AgentOsStudioSectionId =
  | "overview"
  | "explorer"
  | "agents"
  | "skills"
  | "hooks"
  | "tools"
  | "policies"
  | "routines"
  | "loops"
  | "harness"
  | "hosts"
  | "brain"
  | "learning"
  | "sync"
  | "unclassified"
  | "providers"
  | "runs"
  | "systems";

export interface AgentOsStudioSection {
  id: AgentOsStudioSectionId;
  label: string;
  purpose: string;
  modes: AgentOsStudioMode[];
}

export const AGENT_OS_STUDIO_SECTIONS: ReadonlyArray<AgentOsStudioSection> = [
  { id: "overview", label: "Übersicht", purpose: "Live-Status, Coverage und kanonischer Überblick", modes: ["owner", "builder", "expert"] },
  { id: "explorer", label: "Explorer", purpose: "Suche, Filter, strukturierte Ansicht und Graph", modes: ["owner", "builder", "expert"] },
  { id: "brain", label: "Wissen", purpose: "Truth, Entscheidungen, Learning und Provenienz", modes: ["owner", "builder", "expert"] },
  { id: "agents", label: "Agents", purpose: "Agenten, Rollen und Subagenten", modes: ["owner", "builder", "expert"] },
  { id: "skills", label: "Skills", purpose: "Versionierte Skill-Objekte und Tests", modes: ["builder", "expert"] },
  { id: "hooks", label: "Hooks", purpose: "Host-Installationen, Trigger und Hook-Drift", modes: ["owner", "builder", "expert"] },
  { id: "tools", label: "Tools", purpose: "Tools und Capability-Matrix", modes: ["builder", "expert"] },
  { id: "policies", label: "Policies", purpose: "Regeln, Approval und Runtime-Grenzen", modes: ["owner", "builder", "expert"] },
  { id: "routines", label: "Routinen", purpose: "Trigger, Bedingungen und Aktionen", modes: ["owner", "builder", "expert"] },
  { id: "loops", label: "Loops", purpose: "Iterationen, Budgets und Recovery", modes: ["builder", "expert"] },
  { id: "harness", label: "Harness", purpose: "Protocol, Policy, Provider und Evidence", modes: ["owner", "builder", "expert"] },
  { id: "hosts", label: "Hosts", purpose: "Belegte Owner-Geräte und Runtime-Präsenz", modes: ["owner", "builder", "expert"] },
  { id: "learning", label: "Learning", purpose: "Proposals, Review und Rollback", modes: ["owner", "builder", "expert"] },
  { id: "runs", label: "Runs", purpose: "Timeline, Tool Calls und Audit", modes: ["owner", "expert"] },
  { id: "sync", label: "Sync & Drift", purpose: "Canonical, Runtime, Client und Event-Stream", modes: ["owner", "builder", "expert"] },
  { id: "unclassified", label: "Unklassifiziert", purpose: "Explizit sichtbare Artefakte ohne Entity-Zuordnung", modes: ["owner", "builder", "expert"] },
  { id: "providers", label: "Provider", purpose: "Modelle, Routing und Golden Tasks", modes: ["builder", "expert"] },
  { id: "systems", label: "Systeme", purpose: "Runtime, Drift und Attestation", modes: ["owner", "expert"] },
];

export interface AgentOsStudioSummaryInput {
  agents: ReadonlyArray<{ id: string }>;
  skills: ReadonlyArray<{ id: string }>;
  routines: ReadonlyArray<{ id: string }>;
  runs: ReadonlyArray<{ id: string; status: string }>;
  pendingLearning: number;
}

export interface AgentOsStudioSummary {
  agents: number;
  skills: number;
  routines: number;
  activeRuns: number;
  pendingLearning: number;
}

export type AgentOsStudioRecord = Record<string, unknown>;

export function sectionRecords(
  section: AgentOsStudioSectionId,
  readModel: {
    agents?: AgentOsStudioRecord[];
    subagents?: AgentOsStudioRecord[];
    skills?: AgentOsStudioRecord[];
    hooks?: AgentOsStudioRecord[];
    tools?: AgentOsStudioRecord[];
    routines?: AgentOsStudioRecord[];
    loops?: AgentOsStudioRecord[];
    harnesses?: AgentOsStudioRecord[];
    hosts?: AgentOsStudioRecord[];
    policies?: AgentOsStudioRecord[];
    brainEntries?: AgentOsStudioRecord[];
    learning?: AgentOsStudioRecord[];
    providers?: AgentOsStudioRecord[];
    runs?: AgentOsStudioRecord[];
  } | null | undefined,
): AgentOsStudioRecord[] {
  if (!readModel) return [];
  const mapping: Partial<Record<AgentOsStudioSectionId, keyof typeof readModel>> = {
    agents: "agents",
    skills: "skills",
    hooks: "hooks",
    tools: "tools",
    policies: "policies",
    routines: "routines",
    loops: "loops",
    harness: "harnesses",
    hosts: "hosts",
    learning: "learning",
    brain: "brainEntries",
    providers: "providers",
    runs: "runs",
    systems: "policies",
  };
  const key = mapping[section];
  return key ? readModel[key] ?? [] : [];
}

export function recordLabel(record: AgentOsStudioRecord): string {
  for (const key of ["label", "name", "id", "title", "model", "status"]) {
    const value = record[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return "unbenanntes Artefakt";
}

export function recordStatus(record: AgentOsStudioRecord): string {
  const value = record.status ?? record.enabled;
  if (typeof value === "boolean") return value ? "enabled" : "disabled";
  return typeof value === "string" && value.trim() ? value : "read-only";
}

export type AgentOsOrbState = "idle" | "planning" | "executing" | "waiting_for_owner" | "recovering" | "failed" | "completed" | "stopped";

export function deriveAgentOsOrbState(runs: AgentOsStudioRecord[] | undefined): AgentOsOrbState {
  const latest = runs?.[0];
  if (!latest) return "idle";
  const status = String(latest.status ?? "").toLowerCase();
  if (["awaiting_approval", "waiting", "waiting_for_owner"].includes(status)) return "waiting_for_owner";
  if (["planning", "queued"].includes(status)) return "planning";
  if (["running", "executing"].includes(status)) return "executing";
  if (["recovering", "retrying"].includes(status)) return "recovering";
  if (["failed", "error"].includes(status)) return "failed";
  if (["stopped", "cancelled"].includes(status)) return "stopped";
  if (["completed", "done", "success"].includes(status)) return "completed";
  return "idle";
}

export function filterAgentOsStudioSections(query: string): AgentOsStudioSection[] {
  const normalized = query.trim().toLocaleLowerCase("de-DE");
  if (!normalized) return [...AGENT_OS_STUDIO_SECTIONS];
  return AGENT_OS_STUDIO_SECTIONS.filter((section) =>
    `${section.label} ${section.purpose}`.toLocaleLowerCase("de-DE").includes(normalized),
  );
}

export function deriveAgentOsStudioSummary(input: AgentOsStudioSummaryInput): AgentOsStudioSummary {
  return {
    agents: input.agents.length,
    skills: input.skills.length,
    routines: input.routines.length,
    activeRuns: input.runs.filter((run) => ["running", "queued", "waiting", "planning", "executing"].includes(run.status)).length,
    pendingLearning: input.pendingLearning,
  };
}
